import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  writeBatch,
  connectFirestoreEmulator,
  orderBy,
  limit
} from 'firebase/firestore';
import { getAuth, signInAnonymously, connectAuthEmulator } from 'firebase/auth';

// -------------------------------------------------------------
// FIREBASE CONFIGURATION
// -------------------------------------------------------------
const firebaseConfig = {
  apiKey: "AIzaSyDUizKZoU8rB83NSCajXUUnBvlzwEA-WLc",
  authDomain: "anti-gravity-school-erp.firebaseapp.com",
  projectId: "anti-gravity-school-erp",
  storageBucket: "anti-gravity-school-erp.firebasestorage.app",
  messagingSenderId: "923509685723",
  appId: "1:923509685723:web:cbeb5c48f13daa1c0b7bfe",
  measurementId: "G-GBNKFQN7E1"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// Local Emulator Support
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  // Use emulators if explicitly requested via query parameter
  const params = new URLSearchParams(window.location.search);
  if (params.get('emulator') === 'true') {
    console.log("Connecting to local Firebase emulators...");
    try {
      connectFirestoreEmulator(db, 'localhost', 8080);
      connectAuthEmulator(auth, 'http://localhost:9099');
    } catch (err) {
      console.warn("Could not connect to emulators:", err);
    }
  } else {
    console.log("Running on localhost using production project. (Append ?emulator=true to use local emulators)");
  }
}

// Authenticate Anonymously to secure Firestore operations
signInAnonymously(auth)
  .then(() => console.log("Authenticated anonymously with Firebase Auth"))
  .catch((err) => console.error("Firebase authentication failed:", err));

// Helper: ID generator mimicking Code.gs
function genID(prefix) {
  return prefix + Date.now().toString(36).toUpperCase() + Math.floor(Math.random() * 100);
}

// Helper: date formats
function todayStr() {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

function monthStr(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

// -------------------------------------------------------------
// FIRESTORE SERVICES IMPLEMENTATION (Matches Code.gs dispatch)
// -------------------------------------------------------------

export async function getStudents(p) {
  const snapshot = await getDocs(collection(db, 'students'));
  let list = [];
  snapshot.forEach(doc => {
    list.push(doc.data());
  });
  if (p && p.status) {
    list = list.filter(s => s.Status === p.status);
  }
  return list;
}

export async function addStudent(p) {
  const settings = await getSettings();
  const classKey = "fee_" + (p.Class || "").toLowerCase().replace(/-/g, "").replace(/ /g, "");
  const defaultFee = Number(settings[classKey]) || 2000;
  const id = (p.StudentID && p.StudentID.trim()) ? p.StudentID.trim() : genID("STU");
  
  const studentData = {
    StudentID: id,
    Name: p.Name || "",
    DOB: p.DOB || "",
    Class: p.Class || "",
    Gender: p.Gender || "",
    FatherGuardianName: p.FatherGuardianName || "",
    MotherName: p.MotherName || "",
    Phone: p.Phone || "",
    Phone2: p.Phone2 || "",
    Address: p.Address || "",
    JoiningDate: p.JoiningDate || todayStr(),
    FeeAmount: Number(p.FeeAmount) || defaultFee,
    Status: "Active",
    ExitDate: "",
    ExitReason: "",
    Notes: p.Notes || "",
    CameraSubscribed: p.CameraSubscribed === true || String(p.CameraSubscribed).toLowerCase() === "true",
    Photo: p.Photo || ""
  };

  await setDoc(doc(db, 'students', id), studentData);

  if (studentData.CameraSubscribed) {
    await logCameraAction(id, studentData.Name, studentData.Class, "Subscribed", "Subscribed at admission");
  }

  await generateFeesForStudent({ studentID: id, fromMonth: studentData.JoiningDate.substring(0, 7) });

  return { studentID: id };
}

export async function updateStudent(p) {
  const oldId = p.oldStudentID;
  const newId = p.StudentID ? p.StudentID.trim() : "";

  if (oldId && newId && oldId !== newId) {
    // 1. Check if the new ID already exists
    const newDocSnap = await getDoc(doc(db, 'students', newId));
    if (newDocSnap.exists()) {
      throw new Error("Student ID already exists: " + newId);
    }
    
    // 2. Fetch the old student data
    const oldDocRef = doc(db, 'students', oldId);
    const oldDocSnap = await getDoc(oldDocRef);
    if (!oldDocSnap.exists()) {
      throw new Error("Student not found: " + oldId);
    }
    const studentData = oldDocSnap.data();
    
    // Apply updates from p to studentData
    const fields = [
      'Name', 'DOB', 'Class', 'Gender', 'FatherGuardianName', 'MotherName', 
      'Phone', 'Phone2', 'Address', 'JoiningDate', 'FeeAmount', 'Notes', 'CameraSubscribed', 'Photo'
    ];
    fields.forEach(f => {
      if (p[f] !== undefined) studentData[f] = p[f];
    });
    studentData.StudentID = newId; // Update student ID field
    
    // 3. Create the new student document
    await setDoc(doc(db, 'students', newId), studentData);
    
    // 4. Update attendance documents
    const attQuery = query(collection(db, 'attendance'), where('StudentID', '==', oldId));
    const attSnap = await getDocs(attQuery);
    for (const d of attSnap.docs) {
      const attData = d.data();
      attData.StudentID = newId;
      const newAttId = d.id.replace(oldId, newId);
      await setDoc(doc(db, 'attendance', newAttId), attData);
      await deleteDoc(d.ref);
    }
    
    // 5. Update fees documents
    const feesQuery = query(collection(db, 'fees'), where('StudentID', '==', oldId));
    const feesSnap = await getDocs(feesQuery);
    for (const d of feesSnap.docs) {
      await updateDoc(d.ref, { StudentID: newId });
    }
    
    // 6. Update receipts documents
    const receiptsQuery = query(collection(db, 'receipts'), where('StudentID', '==', oldId));
    const receiptsSnap = await getDocs(receiptsQuery);
    for (const d of receiptsSnap.docs) {
      await updateDoc(d.ref, { StudentID: newId });
    }
    
    // 7. Update camera logs
    const camLogsQuery = query(collection(db, 'cameraLog'), where('StudentID', '==', oldId));
    const camLogsSnap = await getDocs(camLogsQuery);
    for (const d of camLogsSnap.docs) {
      await updateDoc(d.ref, { StudentID: newId });
    }
    
    // 8. Delete the old student document
    await deleteDoc(oldDocRef);
  } else {
    // Normal update without changing Student ID
    const ref = doc(db, 'students', p.StudentID);
    const data = {};
    const fields = [
      'Name', 'DOB', 'Class', 'Gender', 'FatherGuardianName', 'MotherName', 
      'Phone', 'Phone2', 'Address', 'JoiningDate', 'FeeAmount', 'Notes', 'CameraSubscribed', 'Photo'
    ];
    fields.forEach(f => {
      if (p[f] !== undefined) data[f] = p[f];
    });
    await updateDoc(ref, data);
  }
  
  return true;
}

export async function exitStudent(p) {
  const ref = doc(db, 'students', p.StudentID);
  await updateDoc(ref, {
    Status: "Inactive",
    ExitDate: p.ExitDate || todayStr(),
    ExitReason: p.ExitReason || ""
  });
  return true;
}

export async function promoteStudent(p) {
  const ref = doc(db, 'students', p.StudentID);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Student not found");
  const s = snap.data();
  
  const classMap = { "Pre-Nursery": "Nursery", "Nursery": "LKG", "LKG": "Graduated" };
  const current = s.Class;
  const next = classMap[current] || "Graduated";

  const update = { Class: next };
  if (next === "Graduated") {
    update.Status = "Inactive";
    update.ExitDate = todayStr();
    update.ExitReason = "Graduated";
  } else {
    const settings = await getSettings();
    const classKey = "fee_" + next.toLowerCase().replace(/-/g, "").replace(/ /g, "");
    if (settings[classKey]) update.FeeAmount = Number(settings[classKey]);
  }

  await updateDoc(ref, update);
  return { newClass: next };
}

export async function logCameraAction(studentID, studentName, cls, action, notes) {
  const id = genID("CAM");
  await setDoc(doc(db, 'cameraLog', id), {
    LogID: id,
    StudentID: studentID,
    StudentName: studentName,
    Class: cls,
    Date: todayStr(),
    Action: action,
    Notes: notes || ""
  });
}

export async function toggleCameraCharge(p) {
  const studentRef = doc(db, 'students', p.studentID);
  const snap = await getDoc(studentRef);
  if (!snap.exists()) throw new Error("Student not found");
  const s = snap.data();
  
  const newVal = !s.CameraSubscribed;
  await updateDoc(studentRef, { CameraSubscribed: newVal });

  const action = newVal ? "Subscribed" : "Unsubscribed";
  await logCameraAction(p.studentID, s.Name || "", s.Class || "", action, p.notes || "");

  return { subscribed: newVal };
}

export async function getCameraLog(p) {
  const snap = await getDocs(collection(db, 'cameraLog'));
  let list = [];
  snap.forEach(doc => {
    list.push(doc.data());
  });
  if (p && p.studentID) {
    list = list.filter(r => r.StudentID === p.studentID);
  }
  return list.reverse();
}

export async function getAttendanceForDate(p) {
  const date = (p && p.date) ? p.date : todayStr();
  const cls = (p && p.cls) ? p.cls : "";

  // Get active students
  const studentSnap = await getDocs(collection(db, 'students'));
  let students = [];
  studentSnap.forEach(d => {
    const s = d.data();
    if (s.Status === "Active" && (!cls || s.Class === cls)) {
      students.push(s);
    }
  });

  // Get attendance for this date
  const attSnap = await getDocs(query(collection(db, 'attendance'), where('Date', '==', date)));
  const attMap = {};
  attSnap.forEach(d => {
    const a = d.data();
    attMap[a.StudentID] = a.Status;
  });

  return students.map(s => ({
    StudentID: s.StudentID,
    Name: s.Name,
    Class: s.Class,
    Status: attMap[s.StudentID] || "Present"
  }));
}

export async function getAttendanceHistory(p) {
  const studentID = p.studentID;
  const q = query(collection(db, 'attendance'), where('StudentID', '==', studentID));
  const snap = await getDocs(q);
  const allAtt = [];
  snap.forEach(d => {
    allAtt.push(d.data());
  });

  const byMonth = {};
  allAtt.forEach(a => {
    const month = String(a.Date).substring(0, 7);
    if (!byMonth[month]) byMonth[month] = { present: 0, absent: 0, half: 0, total: 0 };
    byMonth[month].total++;
    if (a.Status === "Present") byMonth[month].present++;
    else if (a.Status === "Absent") byMonth[month].absent++;
    else if (a.Status === "Half Day") byMonth[month].half++;
  });

  const months = Object.keys(byMonth).sort().reverse().slice(0, 3);
  return months.map(m => {
    const d = byMonth[m];
    return { month: m, present: d.present, absent: d.absent, half: d.half, total: d.total };
  });
}

export async function getMonthAttendance(p) {
  const month = (p && p.month) ? p.month : monthStr();
  const snap = await getDocs(collection(db, 'attendance'));
  const list = [];
  snap.forEach(d => {
    const a = d.data();
    if (String(a.Date).substring(0, 7) === month) {
      list.push(a);
    }
  });
  return list;
}

export async function getMonthTeacherAttendance(p) {
  const month = (p && p.month) ? p.month : monthStr();
  const snap = await getDocs(collection(db, 'teacherAttendance'));
  const list = [];
  snap.forEach(d => {
    const a = d.data();
    if (String(a.Date).substring(0, 7) === month) {
      list.push(a);
    }
  });
  return list;
}

export async function saveAttendance(p) {
  const date = p.date || todayStr();
  const batch = writeBatch(db);
  const ts = new Date().toISOString();

  // Create documents keyed by {Date}_{StudentID} to handle overwrites atomically
  p.records.forEach(r => {
    const docId = `${date}_${r.StudentID}`;
    const ref = doc(db, 'attendance', docId);
    batch.set(ref, {
      Date: date,
      StudentID: r.StudentID,
      Status: r.Status,
      Timestamp: ts
    });
  });

  await batch.commit();
  return true;
}

export async function getFees(p) {
  const month = (p && p.month) ? String(p.month).substring(0, 7) : monthStr();
  const snapshot = await getDocs(collection(db, 'fees'));
  const fees = [];
  snapshot.forEach(doc => {
    const f = doc.data();
    const m = String(f.Month).substring(0, 7);
    if (m === month && f.FeeType !== "Camera") {
      fees.push(f);
    }
  });

  const students = await getStudents();
  const stuMap = {};
  students.forEach(s => { stuMap[s.StudentID] = s; });

  return fees.map(f => {
    const s = stuMap[f.StudentID] || {};
    return {
      FeeID: f.FeeID,
      StudentID: f.StudentID,
      Month: String(f.Month).substring(0, 7),
      Amount: f.Amount,
      Status: f.Status,
      PaymentDate: f.PaymentDate,
      Mode: f.Mode,
      Notes: f.Notes,
      FeeType: f.FeeType || "Monthly",
      StudentName: s.Name || "Unknown",
      Class: s.Class || "",
      StudentStatus: s.Status || "",
      Phone: s.Phone || "",
      CameraSubscribed: s.CameraSubscribed === true || String(s.CameraSubscribed).toLowerCase() === "true"
    };
  });
}

export async function getStudentFees(p) {
  const month = p.month ? String(p.month).substring(0, 7) : monthStr();
  const q = query(collection(db, 'fees'), where('StudentID', '==', p.studentID));
  const snap = await getDocs(q);
  const list = [];
  snap.forEach(d => {
    const f = d.data();
    const fMonth = String(f.Month).substring(0, 7);
    if (fMonth === month && f.FeeType !== "Camera") {
      list.push({
        FeeID: f.FeeID,
        Month: f.Month,
        Amount: f.Amount,
        Status: f.Status,
        FeeType: f.FeeType || "Monthly",
        PaymentDate: f.PaymentDate,
        Mode: f.Mode
      });
    }
  });
  return list;
}

export async function generateMonthlyFees(p) {
  const month = (p && p.month) ? String(p.month).substring(0, 7) : monthStr();
  const students = await getStudents({ status: "Active" });
  
  const snap = await getDocs(collection(db, 'fees'));
  const existSet = {};
  snap.forEach(d => {
    const f = d.data();
    const m = String(f.Month).substring(0, 7);
    existSet[`${f.StudentID}_${m}_${f.FeeType || 'Monthly'}`] = true;
  });

  const batch = writeBatch(db);
  let added = 0;

  students.forEach(s => {
    const key = `${s.StudentID}_${month}_Monthly`;
    if (!existSet[key]) {
      const feeID = genID("FEE");
      const ref = doc(db, 'fees', feeID);
      batch.set(ref, {
        FeeID: feeID,
        StudentID: s.StudentID,
        Month: month,
        Amount: Number(s.FeeAmount) || 2000,
        Status: "Unpaid",
        PaymentDate: "",
        Mode: "",
        Notes: "",
        FeeType: "Monthly"
      });
      added++;
    }
  });

  if (added > 0) {
    await batch.commit();
  }
  return { added: added, month: month };
}

export async function generateFeesForStudent(p) {
  const studentRef = doc(db, 'students', p.studentID);
  const stuSnap = await getDoc(studentRef);
  if (!stuSnap.exists()) return;
  const stu = stuSnap.data();

  const snap = await getDocs(query(collection(db, 'fees'), where('StudentID', '==', p.studentID)));
  const existSet = {};
  snap.forEach(d => {
    const f = d.data();
    existSet[`${f.StudentID}_${f.Month}_${f.FeeType || 'Monthly'}`] = true;
  });

  const batch = writeBatch(db);
  const fp = p.fromMonth.split("-");
  const cur = new Date(parseInt(fp[0]), parseInt(fp[1]) - 1, 1);
  const now = new Date();
  let added = false;

  while (cur.getFullYear() < now.getFullYear() || (cur.getFullYear() === now.getFullYear() && cur.getMonth() <= now.getMonth())) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const mStr = `${y}-${m}`;
    const key = `${p.studentID}_${mStr}_Monthly`;
    
    if (!existSet[key]) {
      const feeID = genID("FEE");
      batch.set(doc(db, 'fees', feeID), {
        FeeID: feeID,
        StudentID: p.studentID,
        Month: mStr,
        Amount: Number(stu.FeeAmount) || 2000,
        Status: "Unpaid",
        PaymentDate: "",
        Mode: "",
        Notes: "",
        FeeType: "Monthly"
      });
      added = true;
    }
    cur.setMonth(cur.getMonth() + 1);
  }

  if (added) {
    await batch.commit();
  }
}

export async function markFeePaid(p) {
  const ref = doc(db, 'fees', p.feeID);
  await updateDoc(ref, {
    Status: "Paid",
    PaymentDate: p.paymentDate || todayStr(),
    Mode: p.mode || "Cash"
  });
  return true;
}

export async function markFeesPaid(p) {
  const batch = writeBatch(db);
  const paymentDate = p.paymentDate || todayStr();
  const mode = p.mode || "Cash";
  p.feeIDs.forEach(fid => {
    const ref = doc(db, 'fees', fid);
    batch.update(ref, {
      Status: "Paid",
      PaymentDate: paymentDate,
      Mode: mode
    });
  });
  await batch.commit();
  return true;
}

export async function addFeeCharge(p) {
  const feeID = genID("FEE");
  const month = p.month || monthStr();
  await setDoc(doc(db, 'fees', feeID), {
    FeeID: feeID,
    StudentID: p.studentID,
    Month: month,
    Amount: Number(p.amount) || 0,
    Status: "Unpaid",
    PaymentDate: "",
    Mode: "",
    Notes: p.notes || "",
    FeeType: p.feeType || "Late"
  });
  return true;
}

export async function updateStudentFee(p) {
  // Update student template
  const ref = doc(db, 'students', p.studentID);
  await updateDoc(ref, { FeeAmount: Number(p.feeAmount) });

  // Update unpaid current/future monthly fees
  const q = query(collection(db, 'fees'), where('StudentID', '==', p.studentID), where('Status', '==', 'Unpaid'));
  const snap = await getDocs(q);
  const today = monthStr();
  const batch = writeBatch(db);
  let updated = false;

  snap.forEach(d => {
    const f = d.data();
    if (f.Month >= today && (f.FeeType || "Monthly") === "Monthly") {
      batch.update(doc(db, 'fees', f.FeeID), { Amount: Number(p.feeAmount) });
      updated = true;
    }
  });

  if (updated) {
    await batch.commit();
  }
  return true;
}

export async function getTeachers(p) {
  const snap = await getDocs(collection(db, 'teachers'));
  const list = [];
  snap.forEach(d => {
    list.push(d.data());
  });
  if (p && p.includeInactive) return list;
  return list.filter(t => t.Status !== "Inactive" && t.Name);
}

export async function addTeacher(p) {
  const id = genID("TCH");
  const data = {
    TeacherID: id,
    Name: p.Name || "",
    Class: p.Class || "",
    Salary: Number(p.Salary) || 0,
    JoiningDate: p.JoiningDate || todayStr(),
    ExitDate: "",
    ExitReason: "",
    Status: "Active",
    Notes: p.Notes || ""
  };
  await setDoc(doc(db, 'teachers', id), data);
  return { teacherID: id };
}

export async function updateTeacher(p) {
  const ref = doc(db, 'teachers', p.TeacherID);
  const data = {};
  const fields = ['Name', 'Class', 'Salary', 'JoiningDate', 'ExitDate', 'ExitReason', 'Status', 'Notes'];
  fields.forEach(f => {
    if (p[f] !== undefined) data[f] = p[f];
  });
  await updateDoc(ref, data);
  return true;
}

export async function getTeacherAttendance(p) {
  const date = (p && p.date) ? p.date : todayStr();
  const teachers = await getTeachers();

  const snap = await getDocs(query(collection(db, 'teacherAttendance'), where('Date', '==', date)));
  const attMap = {};
  snap.forEach(d => {
    const a = d.data();
    attMap[a.TeacherID] = a.Status;
  });

  return teachers.map(t => ({
    TeacherID: t.TeacherID,
    Name: t.Name,
    Class: t.Class || '',
    Status: attMap[t.TeacherID] || "Present"
  }));
}

export async function saveTeacherAttendance(p) {
  const date = p.date || todayStr();
  const batch = writeBatch(db);
  const ts = new Date().toISOString();

  p.records.forEach(r => {
    const docId = `${date}_${r.TeacherID}`;
    const ref = doc(db, 'teacherAttendance', docId);
    batch.set(ref, {
      Date: date,
      TeacherID: r.TeacherID,
      Status: r.Status,
      Timestamp: ts
    });
  });

  await batch.commit();
  return true;
}

export async function getSalary(p) {
  const month = (p && p.month) ? p.month : monthStr();
  const snapshot = await getDocs(collection(db, 'salary'));
  const salaries = [];
  snapshot.forEach(doc => {
    const s = doc.data();
    if (s.Month === month) {
      salaries.push(s);
    }
  });

  const teachers = await getTeachers({ includeInactive: true });
  const tMap = {};
  teachers.forEach(t => { tMap[t.TeacherID] = t; });

  return salaries.map(s => ({
    ...s,
    Name: tMap[s.TeacherID]?.Name || "Unknown"
  }));
}

export async function computeSalary(p) {
  const month = (p && p.month) ? p.month : monthStr();
  const teachers = await getTeachers();
  if (teachers.length === 0) return [];

  const tAttSnap = await getDocs(collection(db, 'teacherAttendance'));
  const monthAtt = [];
  tAttSnap.forEach(d => {
    const a = d.data();
    if (String(a.Date).substring(0, 7) === month) {
      monthAtt.push(a);
    }
  });

  const ym = month.split("-");
  const yr = parseInt(ym[0]), mo = parseInt(ym[1]);
  const daysInMonth = new Date(yr, mo, 0).getDate();
  let workingDays = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    if (new Date(yr, mo - 1, d).getDay() !== 0) workingDays++;
  }

  const salSnap = await getDocs(collection(db, 'salary'));
  const existMap = {};
  salSnap.forEach(d => {
    const s = d.data();
    existMap[`${s.TeacherID}_${s.Month}`] = { salaryID: s.SalaryID, status: s.Status };
  });

  const results = [];
  const batch = writeBatch(db);

  for (const t of teachers) {
    const key = `${t.TeacherID}_${month}`;
    const tAtt = monthAtt.filter(a => a.TeacherID === t.TeacherID);
    const present = tAtt.filter(a => a.Status === "Present").length;
    const halfDays = tAtt.filter(a => a.Status === "Half Day").length;
    const leaves = tAtt.filter(a => a.Status === "Leave").length;
    
    const payableDays = tAtt.length === 0 ? workingDays : (present + halfDays * 0.5);
    const baseSalary = Number(t.Salary) || 0;
    const finalSalary = workingDays > 0 ? Math.round((baseSalary / workingDays) * payableDays) : baseSalary;

    if (existMap[key]) {
      const existing = existMap[key];
      if (existing.status !== "Paid") {
        batch.update(doc(db, 'salary', existing.salaryID), {
          WorkingDays: workingDays,
          DaysPresent: present,
          Leaves: leaves,
          HalfDays: halfDays,
          FinalSalary: finalSalary
        });
      }
      results.push({
        SalaryID: existing.salaryID,
        TeacherID: t.TeacherID,
        Name: t.Name,
        Month: month,
        WorkingDays: workingDays,
        DaysPresent: present,
        Leaves: leaves,
        HalfDays: halfDays,
        FinalSalary: finalSalary,
        Status: existing.status
      });
    } else {
      const salaryID = genID("SAL");
      const data = {
        SalaryID: salaryID,
        TeacherID: t.TeacherID,
        Month: month,
        WorkingDays: workingDays,
        DaysPresent: present,
        Leaves: leaves,
        HalfDays: halfDays,
        FinalSalary: finalSalary,
        Status: "Unpaid",
        PaidDate: ""
      };
      batch.set(doc(db, 'salary', salaryID), data);
      results.push({
        ...data,
        Name: t.Name
      });
    }
  }

  await batch.commit();
  return results;
}

export async function markSalaryPaid(p) {
  const ref = doc(db, 'salary', p.salaryID);
  await updateDoc(ref, {
    Status: "Paid",
    PaidDate: p.paidDate || todayStr()
  });
  return true;
}

export async function getActivity(p) {
  const snap = await getDocs(collection(db, 'activityLog'));
  let list = [];
  snap.forEach(d => {
    list.push(d.data());
  });
  if (p && p.month) {
    list = list.filter(a => String(a.Date).substring(0, 7) === p.month);
  }
  if (p && p.cls) {
    list = list.filter(a => a.Class === p.cls || !a.Class);
  }
  return list;
}

export async function addActivity(p) {
  const id = genID("ACT");
  await setDoc(doc(db, 'activityLog', id), {
    LogID: id,
    Date: p.Date || todayStr(),
    Class: p.Class || "",
    Keyword: p.Keyword || "",
    Notes: p.Notes || "",
    Timestamp: new Date().toISOString()
  });
  return true;
}

export async function getSettings() {
  const snap = await getDoc(doc(db, 'settings', 'global'));
  if (snap.exists()) {
    const data = snap.data();
    if (data.school_name === "Anti Gravity School" || data.school_name === "Anti Gravity School ERP") {
      data.school_name = "Tiny Detectives School";
      await saveSettings(data);
    }
    return data;
  }
  // Default values
  return { 
    fee_prenursery: "1800",
    fee_nursery: "2000",
    fee_lkg: "2400",
    camera_charge: "500",
    school_name: "Tiny Detectives School"
  };
}

export async function saveSettings(p) {
  const ref = doc(db, 'settings', 'global');
  const current = await getSettings();
  await setDoc(ref, {
    ...current,
    ...p
  });
  return true;
}

export async function getHolidays(p) {
  const snap = await getDocs(collection(db, 'holidays'));
  let list = [];
  snap.forEach(d => {
    list.push(d.data());
  });
  if (p && p.month) {
    list = list.filter(h => String(h.Date).substring(0, 7) === p.month);
  }
  return list.sort((a, b) => a.Date < b.Date ? -1 : 1);
}

export async function saveHoliday(p) {
  if (p.HolidayID) {
    const ref = doc(db, 'holidays', p.HolidayID);
    await updateDoc(ref, {
      Date: p.Date || "",
      Name: p.Name || "",
      Type: p.Type || "Holiday",
      Notes: p.Notes || ""
    });
    return { HolidayID: p.HolidayID };
  }
  
  const id = genID("HOL");
  await setDoc(doc(db, 'holidays', id), {
    HolidayID: id,
    Date: p.Date || todayStr(),
    Name: p.Name || "",
    Type: p.Type || "Holiday",
    Notes: p.Notes || ""
  });
  return { HolidayID: id };
}

export async function deleteHoliday(p) {
  await deleteDoc(doc(db, 'holidays', p.HolidayID));
  return true;
}

export async function getNextReceiptNo() {
  const snapshot = await getDocs(collection(db, 'feeReceipts'));
  let maxNo = 517;
  snapshot.forEach(doc => {
    const r = doc.data();
    const n = parseInt(String(r.ReceiptNo).replace(/\D/g, "")) || 0;
    if (n > maxNo) maxNo = n;
  });
  const next = String(maxNo + 1).padStart(4, "0");
  return { nextNo: next };
}

export async function getReceipts(p) {
  const snap = await getDocs(collection(db, 'feeReceipts'));
  let list = [];
  snap.forEach(d => {
    list.push(d.data());
  });
  if (p && p.studentID) {
    list = list.filter(r => r.StudentID === p.studentID);
  }
  // Sort descending by Timestamp or Date and return top 50
  return list.sort((a,b) => b.Timestamp.localeCompare(a.Timestamp)).slice(0, 50);
}

export async function saveReceipt(p) {
  const id = genID("RCP");
  const itemsStr = JSON.stringify(p.items || []);

  const batch = writeBatch(db);

  // Write the receipt
  const receiptRef = doc(db, 'feeReceipts', id);
  batch.set(receiptRef, {
    ReceiptID: id,
    ReceiptNo: p.receiptNo || "0518",
    StudentID: p.studentID || "",
    StudentName: p.studentName || "",
    ParentName: p.parentName || "",
    Class: p.class_ || "",
    Date: p.date || todayStr(),
    Items: itemsStr,
    TotalAmount: p.totalAmount || 0,
    Notes: p.notes || "",
    Timestamp: new Date().toISOString()
  });

  // Auto-mark linked fee documents as Paid
  if (p.feeIDs && p.feeIDs.length > 0) {
    const paidDate = p.date || todayStr();
    const mode = p.mode || "Cash";
    p.feeIDs.forEach(fid => {
      const feeRef = doc(db, 'fees', fid);
      batch.update(feeRef, {
        Status: "Paid",
        PaymentDate: paidDate,
        Mode: mode
      });
    });
  }

  await batch.commit();
  return { receiptID: id };
}

// -------------------------------------------------------------
// GET ALL DATA BUNDLE FOR CACHE WARMING
// -------------------------------------------------------------
export async function getAllData(p) {
  const today = todayStr();
  const currentMonth = monthStr();

  // Load all components in parallel
  const [
    students,
    attSnap,
    tattSnap,
    feesSnap,
    teachers,
    actSnap,
    settings,
    receiptNoObj,
    holidays
  ] = await Promise.all([
    getStudents(),
    getDocs(collection(db, 'attendance')),
    getDocs(collection(db, 'teacherAttendance')),
    getDocs(collection(db, 'fees')),
    getTeachers(),
    getDocs(collection(db, 'activityLog')),
    getSettings(),
    getNextReceiptNo(),
    getHolidays()
  ]);

  const attendance = [];
  attSnap.forEach(d => attendance.push(d.data()));

  const teacherAtt = [];
  tattSnap.forEach(d => teacherAtt.push(d.data()));

  const fees = [];
  feesSnap.forEach(d => {
    const f = d.data();
    if (f.FeeType !== "Camera") {
      fees.push(f);
    }
  });

  const activity = [];
  actSnap.forEach(d => {
    const a = d.data();
    if (String(a.Date).substring(0, 7) === currentMonth) {
      activity.push(a);
    }
  });

  return {
    students,
    attendance,
    teacherAtt,
    fees,
    teachers,
    activity,
    settings,
    nextReceiptNo: receiptNoObj.nextNo,
    holidays,
    fetchedAt: today
  };
}

// -------------------------------------------------------------
// BULK DATA MIGRATION IMPORT
// -------------------------------------------------------------
export async function importData(p) {
  const data = p.data;
  const batchLimit = 500;
  let batch = writeBatch(db);
  let opCount = 0;

  async function checkCommit() {
    opCount++;
    if (opCount >= batchLimit) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  const stats = {
    settings: 0,
    students: 0,
    attendance: 0,
    fees: 0,
    teachers: 0,
    teacherAttendance: 0,
    salary: 0,
    activityLog: 0,
    feeReceipts: 0,
    cameraLog: 0,
    holidays: 0
  };

  // Helper to find case-insensitive keys with or without spaces
  function getVal(row, keys) {
    for (const key of keys) {
      if (row[key] !== undefined) return row[key];
      // try replacing spaces, dashes, or changing case
      const normalizedKey = key.toLowerCase().replace(/[\s-_]/g, "");
      for (const k in row) {
        if (k.toLowerCase().replace(/[\s-_]/g, "") === normalizedKey) {
          return row[k];
        }
      }
    }
    return undefined;
  }

  // Helper to find sheets case-insensitively and space-insensitively
  function getSheetData(dataObj, sheetNames) {
    for (const name of sheetNames) {
      if (dataObj[name] && Array.isArray(dataObj[name])) return dataObj[name];
      const normalized = name.toLowerCase().replace(/[\s-_]/g, "");
      for (const k in dataObj) {
        if (k.toLowerCase().replace(/[\s-_]/g, "") === normalized) {
          if (Array.isArray(dataObj[k])) return dataObj[k];
        }
      }
    }
    return null;
  }

  // 1. Settings (Key/Value sheet)
  const settingsData = getSheetData(data, ['Settings', 'settings']);
  if (settingsData) {
    const settingsObj = {};
    settingsData.forEach(row => {
      const key = getVal(row, ['Key', 'key']);
      const value = getVal(row, ['Value', 'value']);
      if (key) {
        settingsObj[key] = value;
        stats.settings++;
      }
    });
    if (!settingsObj.school_name) settingsObj.school_name = "Tiny Detectives School";
    batch.set(doc(db, 'settings', 'global'), settingsObj);
    await checkCommit();
  }

  // 2. Students
  const studentsData = getSheetData(data, ['Students', 'students']);
  if (studentsData) {
    studentsData.forEach(row => {
      const id = getVal(row, ['StudentID', 'Student ID', 'studentID']);
      if (id) {
        batch.set(doc(db, 'students', id), {
          StudentID: id,
          Name: getVal(row, ['Name', 'name']) || "",
          DOB: getVal(row, ['DOB', 'dob', 'Date of Birth', 'Date Of Birth']) || "",
          Class: getVal(row, ['Class', 'class']) || "",
          Gender: getVal(row, ['Gender', 'gender']) || "",
          FatherGuardianName: getVal(row, ['FatherGuardianName', 'fatherGuardianName', 'Father/Guardian Name', 'Father Name', 'Guardian Name']) || "",
          MotherName: getVal(row, ['MotherName', 'motherName', "Mother's Name"]) || "",
          Phone: String(getVal(row, ['Phone', 'phone', 'Mobile', 'Mobile Number', 'Contact']) || ""),
          Phone2: String(getVal(row, ['Phone2', 'phone2', 'Secondary Phone']) || ""),
          Address: getVal(row, ['Address', 'address']) || "",
          JoiningDate: getVal(row, ['JoiningDate', 'joiningDate', 'Joining Date']) || "",
          FeeAmount: Number(getVal(row, ['FeeAmount', 'feeAmount', 'Fee Amount', 'Fee'])) || 2000,
          Status: getVal(row, ['Status', 'status']) || "Active",
          ExitDate: getVal(row, ['ExitDate', 'exitDate', 'Exit Date']) || "",
          ExitReason: getVal(row, ['ExitReason', 'exitReason', 'Exit Reason']) || "",
          Notes: getVal(row, ['Notes', 'notes']) || "",
          CameraSubscribed: getVal(row, ['CameraSubscribed', 'cameraSubscribed', 'Camera Subscribed']) === true || String(getVal(row, ['CameraSubscribed', 'cameraSubscribed', 'Camera Subscribed'])).toLowerCase() === "true"
        });
        stats.students++;
        opCount++;
      }
    });
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 3. Attendance
  const attendanceData = getSheetData(data, ['Attendance', 'attendance']);
  if (attendanceData) {
    for (const row of attendanceData) {
      const date = getVal(row, ['Date', 'date']);
      const studentID = getVal(row, ['StudentID', 'Student ID', 'studentID']);
      if (date && studentID) {
        const docId = `${date}_${studentID}`;
        batch.set(doc(db, 'attendance', docId), {
          Date: date,
          StudentID: studentID,
          Status: getVal(row, ['Status', 'status']) || "Present",
          Timestamp: getVal(row, ['Timestamp', 'timestamp']) || new Date().toISOString()
        });
        stats.attendance++;
        await checkCommit();
      }
    }
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 4. Fees
  const feesData = getSheetData(data, ['Fees', 'fees']);
  if (feesData) {
    for (const row of feesData) {
      let feeID = getVal(row, ['FeeID', 'Fee ID', 'feeID']);
      if (!feeID) feeID = genID("FEE");
      if (feeID) {
        batch.set(doc(db, 'fees', feeID), {
          FeeID: feeID,
          StudentID: getVal(row, ['StudentID', 'Student ID', 'studentID']) || "",
          Month: getVal(row, ['Month', 'month']) || "",
          Amount: Number(getVal(row, ['Amount', 'amount'])) || 0,
          Status: getVal(row, ['Status', 'status']) || "Unpaid",
          PaymentDate: getVal(row, ['PaymentDate', 'paymentDate', 'Payment Date']) || "",
          Mode: getVal(row, ['Mode', 'mode']) || "",
          Notes: getVal(row, ['Notes', 'notes']) || "",
          FeeType: getVal(row, ['FeeType', 'feeType', 'Fee Type']) || "Monthly"
        });
        stats.fees++;
        await checkCommit();
      }
    }
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 5. Teachers
  const teachersData = getSheetData(data, ['Teachers', 'teachers']);
  if (teachersData) {
    teachersData.forEach(row => {
      let id = getVal(row, ['TeacherID', 'Teacher ID', 'teacherID']);
      if (!id) id = genID("TCH");
      if (id) {
        batch.set(doc(db, 'teachers', id), {
          TeacherID: id,
          Name: getVal(row, ['Name', 'name']) || "",
          Class: getVal(row, ['Class', 'class']) || "",
          Salary: Number(getVal(row, ['Salary', 'salary'])) || 0,
          JoiningDate: getVal(row, ['JoiningDate', 'joiningDate', 'Joining Date']) || "",
          ExitDate: getVal(row, ['ExitDate', 'exitDate', 'Exit Date']) || "",
          ExitReason: getVal(row, ['ExitReason', 'exitReason', 'Exit Reason']) || "",
          Status: getVal(row, ['Status', 'status']) || "Active",
          Notes: getVal(row, ['Notes', 'notes']) || ""
        });
        stats.teachers++;
        opCount++;
      }
    });
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 6. Teacher Attendance
  const teacherAttendanceData = getSheetData(data, ['TeacherAttendance', 'Teacher Attendance', 'teacherattendance', 'teacher_attendance']);
  if (teacherAttendanceData) {
    for (const row of teacherAttendanceData) {
      const date = getVal(row, ['Date', 'date']);
      const teacherID = getVal(row, ['TeacherID', 'Teacher ID', 'teacherID']);
      if (date && teacherID) {
        const docId = `${date}_${teacherID}`;
        batch.set(doc(db, 'teacherAttendance', docId), {
          Date: date,
          TeacherID: teacherID,
          Status: getVal(row, ['Status', 'status']) || "Present",
          Timestamp: getVal(row, ['Timestamp', 'timestamp']) || new Date().toISOString()
        });
        stats.teacherAttendance++;
        await checkCommit();
      }
    }
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 7. Salary
  const salaryData = getSheetData(data, ['Salary', 'salary']);
  if (salaryData) {
    salaryData.forEach(row => {
      let salaryID = getVal(row, ['SalaryID', 'Salary ID', 'salaryID']);
      if (!salaryID) salaryID = genID("SAL");
      if (salaryID) {
        batch.set(doc(db, 'salary', salaryID), {
          SalaryID: salaryID,
          TeacherID: getVal(row, ['TeacherID', 'Teacher ID', 'teacherID']) || "",
          Month: getVal(row, ['Month', 'month']) || "",
          WorkingDays: Number(getVal(row, ['WorkingDays', 'workingDays', 'Working Days'])) || 24,
          DaysPresent: Number(getVal(row, ['DaysPresent', 'daysPresent', 'Days Present'])) || 24,
          Leaves: Number(getVal(row, ['Leaves', 'leaves'])) || 0,
          HalfDays: Number(getVal(row, ['HalfDays', 'halfDays', 'Half Days'])) || 0,
          FinalSalary: Number(getVal(row, ['FinalSalary', 'finalSalary', 'Final Salary'])) || 0,
          Status: getVal(row, ['Status', 'status']) || "Unpaid",
          PaidDate: getVal(row, ['PaidDate', 'paidDate', 'Paid Date']) || ""
        });
        stats.salary++;
        opCount++;
      }
    });
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 8. ActivityLog
  const activityLogData = getSheetData(data, ['ActivityLog', 'Activity Log', 'activitylog', 'activity_log']);
  if (activityLogData) {
    activityLogData.forEach(row => {
      let logID = getVal(row, ['LogID', 'Log ID', 'logID']);
      if (!logID) logID = genID("ACT");
      if (logID) {
        batch.set(doc(db, 'activityLog', logID), {
          LogID: logID,
          Date: getVal(row, ['Date', 'date']) || "",
          Class: getVal(row, ['Class', 'class']) || "",
          Keyword: getVal(row, ['Keyword', 'keyword']) || "",
          Notes: getVal(row, ['Notes', 'notes']) || "",
          Timestamp: getVal(row, ['Timestamp', 'timestamp']) || new Date().toISOString()
        });
        stats.activityLog++;
        opCount++;
      }
    });
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 9. FeeReceipts
  const feeReceiptsData = getSheetData(data, ['FeeReceipts', 'Fee Receipts', 'feereceipts', 'fee_receipts']);
  if (feeReceiptsData) {
    for (const row of feeReceiptsData) {
      let receiptID = getVal(row, ['ReceiptID', 'Receipt ID', 'receiptID']);
      if (!receiptID) receiptID = genID("RCP");
      if (receiptID) {
        batch.set(doc(db, 'feeReceipts', receiptID), {
          ReceiptID: receiptID,
          ReceiptNo: getVal(row, ['ReceiptNo', 'Receipt No', 'receiptNo']) || "",
          StudentID: getVal(row, ['StudentID', 'Student ID', 'studentID']) || "",
          StudentName: getVal(row, ['StudentName', 'Student Name', 'studentName']) || "",
          ParentName: getVal(row, ['ParentName', 'Parent Name', 'parentName']) || "",
          Class: getVal(row, ['Class', 'class']) || "",
          Date: getVal(row, ['Date', 'date']) || "",
          Items: typeof getVal(row, ['Items', 'items']) === 'string' ? getVal(row, ['Items', 'items']) : JSON.stringify(getVal(row, ['Items', 'items']) || []),
          TotalAmount: Number(getVal(row, ['TotalAmount', 'totalAmount', 'Total Amount'])) || 0,
          Notes: getVal(row, ['Notes', 'notes']) || "",
          Timestamp: getVal(row, ['Timestamp', 'timestamp']) || new Date().toISOString()
        });
        stats.feeReceipts++;
        await checkCommit();
      }
    }
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 10. CameraLog
  const cameraLogData = getSheetData(data, ['CameraLog', 'Camera Log', 'cameralog', 'camera_log']);
  if (cameraLogData) {
    cameraLogData.forEach(row => {
      let logID = getVal(row, ['LogID', 'Log ID', 'logID']);
      if (!logID) logID = genID("CAM");
      if (logID) {
        batch.set(doc(db, 'cameraLog', logID), {
          LogID: logID,
          StudentID: getVal(row, ['StudentID', 'Student ID', 'studentID']) || "",
          StudentName: getVal(row, ['StudentName', 'Student Name', 'studentName']) || "",
          Class: getVal(row, ['Class', 'class']) || "",
          Date: getVal(row, ['Date', 'date']) || "",
          Action: getVal(row, ['Action', 'action']) || "",
          Notes: getVal(row, ['Notes', 'notes']) || ""
        });
        stats.cameraLog++;
        opCount++;
      }
    });
    if (opCount > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    }
  }

  // 11. Holidays
  const holidaysData = getSheetData(data, ['Holidays', 'Holiday List', 'HolidayList', 'holidays', 'holiday_list', 'holidaylist']);
  if (holidaysData) {
    holidaysData.forEach(row => {
      let id = getVal(row, ['HolidayID', 'Holiday ID', 'holidayID']);
      if (!id) id = genID("HOL");
      if (id) {
        batch.set(doc(db, 'holidays', id), {
          HolidayID: id,
          Date: getVal(row, ['Date', 'date', 'StartDate', 'startDate', 'Start Date', 'start date']) || "",
          StartDate: getVal(row, ['StartDate', 'startDate', 'Start Date', 'start date', 'Date', 'date']) || "",
          EndDate: getVal(row, ['EndDate', 'endDate', 'End Date', 'end date', 'Date', 'date']) || "",
          Occasion: getVal(row, ['Occasion', 'occasion', 'Occassion', 'occassion', 'Name', 'name']) || "",
          Name: getVal(row, ['Name', 'name', 'Occasion', 'occasion', 'Occassion', 'occassion']) || "",
          Type: getVal(row, ['Type', 'type']) || "Holiday",
          NoOfDays: Number(getVal(row, ['NoOfDays', 'noOfDays', 'No of Days', 'No Of Days', 'No. of days'])) || 1,
          Days: getVal(row, ['Days', 'days']) || "",
          Notes: getVal(row, ['Notes', 'notes']) || ""
        });
        stats.holidays++;
        opCount++;
      }
    });
    if (opCount > 0) {
      await batch.commit();
    }
  }

  return stats;
}

// Global dispatcher to mimic GAS Code.gs dispatch function
export async function dispatchFirebase(action, payload) {
  const functions = {
    getStudents,
    addStudent,
    updateStudent,
    exitStudent,
    promoteStudent,
    getAttendance: getAttendanceForDate,
    getAttendanceForDate,
    getAttendanceHistory,
    getMonthAttendance,
    getMonthTeacherAttendance,
    saveAttendance,
    getFees,
    getStudentFees,
    markFeePaid,
    markFeesPaid,
    generateMonthlyFees,
    updateStudentFee,
    addFeeCharge,
    toggleCameraCharge,
    getTeachers,
    addTeacher,
    updateTeacher,
    getTeacherAttendance,
    saveTeacherAttendance,
    getSalary,
    markSalaryPaid,
    computeSalary,
    getActivity,
    addActivity,
    getSettings,
    saveSettings,
    getReceipts,
    saveReceipt,
    getNextReceiptNo,
    getHolidays,
    saveHoliday,
    deleteHoliday,
    getAllData,
    getCameraLog,
    importData
  };

  const fn = functions[action];
  if (!fn) {
    throw new Error("Unknown firebase action: " + action);
  }

  try {
    const data = await fn(payload);
    return data;
  } catch (err) {
    console.error(`Firebase error [${action}]:`, err);
    throw err;
  }
}
